// --- React & Redux
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
// --- External UI Libraries
import { Drawer } from 'antd';
import Button from '@dtci/spellbook/Button';
// --- API
import { datastageApi } from 'datastage/api';
// --- Hooks & Store
import { useDealVersionActions } from 'datastage/store/dealVersion/useDealVersion';
import { useLoading } from 'datastage/utils/hooks/useLoading';
import { useToasts } from 'datastage/utils/hooks/useToasts';
// --- Components
import { DealStatusForm } from './deal-status-form';
// --- Styles
import './styles.scss';

/**
 * Deal Status Drawer
 *
 * * Features:
 * * - Displays a drawer for updating deal status
 * * - Integrates with Redux for state management
 * * - Handles API calls to update deal status
 *
 * @returns {Element}
 * @constructor
 */
export const DealStatusDrawer = () => {
    const { isDealStatusOpen, selectedDeal, selectedStatus } = useSelector(
        (state) => state.dealVersion,
    );
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const { showErrorToast, showSuccessToast } = useToasts();
    const { updateDealVersion } = useDealVersionActions();

    const handleClose = useCallback(() => {
        updateDealVersion({ isDealStatusOpen: false });
    }, [updateDealVersion]);

    // Handle updating the deal status via API
    const handleUpdateDealStatus = async () => {
        if (!selectedDeal) return;

        setLoadingTrue();
        try {
            const res = await datastageApi.updateDealStatus({
                dealId: selectedDeal.id,
                status: selectedStatus.id,
                templateName: selectedDeal.template,
            });

            if (res?.code) {
                showErrorToast(res?.message || 'Failed to update deal status.');
                return;
            }

            showSuccessToast('Deal status updated successfully.');
            handleClose();
            // Trigger grid refresh
            updateDealVersion({ refreshDealVersion: true });
        } catch (error) {
            showErrorToast(error?.message || 'Error updating deal status.');
            console.error('Error updating deal status:', error);
        } finally {
            setLoadingFalse();
        }
    };

    return (
        <Drawer
            className="deal-status-drawer"
            title="Deal Versioning Solutioning"
            width={600}
            placement="right"
            open={isDealStatusOpen}
            onClose={handleClose}
            footer={
                <div className="button-row">
                    <Button
                        iconType="fa-solid fa-xmark"
                        labelText="close"
                        styleType="link"
                        onClick={handleClose}
                    >
                        Close
                    </Button>
                    <Button
                        iconType="fa-solid fa-wrench"
                        labelText="create"
                        styleType="link"
                        onClick={handleUpdateDealStatus}
                        disabled={!selectedDeal}
                    >
                        Update
                    </Button>
                </div>
            }
        >
            <DealStatusForm />
        </Drawer>
    );
};
